import React, { useState, useEffect } from 'react';
import styles from './style.css';
import cn from 'classnames';
import Button from 'chameleon/ui-stack/ui/base/button';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import CheckBox from 'chameleon/ui-stack/ui/base/checkbox';
import { FlyoutSecondMenu } from 'chameleon/ui-stack/ui/assessments/actions/FlyoutSecondMenu';
import LabelListMenu from 'legacy-src/components/TeacherOrganize/components/LabelListMenu';
import { useTeacherOrganizeContext } from 'legacy-src/components/TeacherOrganize/hooks/useTeacherOrganize';
import { toast } from 'chameleon/ui-stack/ui/toast';
import { BulkLabelNotification } from './utils';
import {
  addMultipleLabelItemsBulk,
  addMultipleLabelResourcesBulk,
} from '../../hooks/utils/api.utils';
import { BULK_LABEL_DATA_ID } from './const';
import { resourceTypes } from 'chameleon/legacy/src/components/TeacherOrganize/utils/constants';

const keysToArray = (obj) => Object.keys(obj).filter((key) => obj[key]);

const BulkToolbar = ({
  data = [],
  pageSize = 30,
  page = 0,
  masterSubject = '',
  onBulkLabelUpdate = () => {},
  disableBulkCheckbox = false,
}) => {
  const {
    labels,
    parents,
    children,
    state: { resourcesForBulk, isBulkMode, resourcesForBulkLength },
    actions: {
      deactivateBulkMode,
      addOrRemoveBulk,
      checkIfResourceSelectedBulk,
      clearBulkSelections,
    },
  } = useTeacherOrganizeContext({ masterSubject });

  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const resourceOnPage = data
    .slice(pageSize * page, pageSize * (page + 1))
    .filter(({ type }) => type !== resourceTypes.pdf);
  const isAllResourceOnPageSelected = resourceOnPage.every(({ id }) =>
    checkIfResourceSelectedBulk(id),
  );

  const toggleAll = () => {
    resourceOnPage.forEach(({ id, type }) =>
      addOrRemoveBulk(id, type, isAllResourceOnPageSelected),
    );
  };

  const addLabelsToBulk = (selected) => {
    const orderedLabels = parents?.reduce(
      (result, parent) => [...result, parent, ...(children[parent] || [])],
      [],
    );

    orderedLabels &&
      setSelectedLabelIds(orderedLabels.filter((labelId) => selected[labelId]));
  };

  const persistLabels = async () => {
    const toSave = {
      labelIds: selectedLabelIds,
      itemIds: keysToArray(resourcesForBulk.item),
      resourceIds: keysToArray(resourcesForBulk.video),
      pdfIds: keysToArray(resourcesForBulk.pdf),
    };

    const [bulkItemsResponse, bulkResourcesResponse] = await Promise.all([
      toSave.itemIds.length ? addMultipleLabelItemsBulk(toSave) : null,
      toSave.resourceIds.length ? addMultipleLabelResourcesBulk(toSave) : null,
    ]);

    if (
      (bulkItemsResponse && bulkItemsResponse.success) ||
      (bulkResourcesResponse && bulkResourcesResponse.success)
    ) {
      // Toast notification after successful Add
      selectedLabelIds.forEach((labelId) => {
        const toastNotificationMessage = BulkLabelNotification({
          name: labels[labelId]?.name,
          resourcesCount: resourcesForBulkLength,
        });

        toast(toastNotificationMessage.added);
      });

      // Label selections and resource checkboxes are all cleared out
      onBulkLabelUpdate({
        resources: resourcesForBulk,
        labelIds: selectedLabelIds,
      });
      setSelectedLabelIds([]);
      clearBulkSelections();
    }
  };

  useEffect(() => {
    !isBulkMode && setSelectedLabelIds([]);
  }, [isBulkMode]);

  const formatLabelsForUI = () => {
    return selectedLabelIds.map(
      (id, index) =>
        `${labels[id]?.name}${
          selectedLabelIds.length > 1 && index < selectedLabelIds.length - 1
            ? ', '
            : ''
        }`,
    );
  };
  return isBulkMode ? (
    <div
      className={cn(
        styles.customPosition,
        'bg-blue-700 flex items-center px-8.2 text-white justify-between sticky z-10 top-0',
      )}
    >
      <div className="flex items-center text-white text-14 pl-8 flex-1 max-w-1/2">
        <div className="w-8.2 mt-2">
          <CheckBox
            accessibility={{
              title: `${
                isAllResourceOnPageSelected ? 'Deselect' : 'Select'
              } all resources on the page to bulk mode`,
            }}
            checked={isAllResourceOnPageSelected && resourcesForBulkLength > 0}
            onClick={toggleAll}
            disabled={disableBulkCheckbox}
            data-test-id={BULK_LABEL_DATA_ID.checkBox}
          />
        </div>

        <span
          className="ml-4 pr-4 border-r border-white border-opacity-25 whitespace-nowrap"
          data-test-id={BULK_LABEL_DATA_ID.bulkCheckBoxText}
        >
          {resourcesForBulkLength} Selected
        </span>

        <span className="pl-4 whitespace-nowrap">Label with</span>

        <div className="max-w-full mx-4.5">
          <FlyoutSecondMenu
            isNarrow
            fallBackPlacements={[]}
            position={'bottom'}
            trigger={(buttonProps, isOpen, reference) => {
              return (
                <ButtonWithIcon
                  iconOrientation="right"
                  icon="arrow_drop_down"
                  ref={reference}
                  {...buttonProps}
                  className="max-w-full"
                  containerClassName="truncate"
                  data-test-id={BULK_LABEL_DATA_ID.dropDown}
                >
                  {selectedLabelIds.length > 0
                    ? formatLabelsForUI()
                    : 'Select Label'}
                </ButtonWithIcon>
              );
            }}
          >
            <LabelListMenu
              showBulkAction={false}
              showCreateNewLabelAction={false}
              onClose={addLabelsToBulk}
              assignedLabels={selectedLabelIds}
            />
          </FlyoutSecondMenu>
        </div>

        <Button
          disabled={
            selectedLabelIds.length === 0 || resourcesForBulkLength === 0
          }
          onClick={persistLabels}
          dataTestId={BULK_LABEL_DATA_ID.add}
        >
          Add
        </Button>
      </div>

      <div className="pr-8">
        <ButtonWithIcon icon="exit" onClick={() => deactivateBulkMode()}>
          Exit Bulk Label
        </ButtonWithIcon>
      </div>
    </div>
  ) : null;
};

export default BulkToolbar;
